import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormRecord,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { GetAnswerRequest } from '../../../../../../backend/src/core/openai/openai.gate';
import { Methodology } from '../../../core/methodology/methodology';
import { OpenaiModel } from '../../../core/openai/openai';
import { MethodologyService } from './../../../core/methodology/methodology.service';
import { OpenaiService } from './../../../core/openai/openai.service';

@Component({
  selector: 'app-new-edit-methodology',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzListModule,
  ],
  templateUrl: './new-edit-methodology.component.html',
  styleUrl: './new-edit-methodology.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewEditMethodologyComponent {
  @Input() selectedItem?: Methodology;
  @Input() isNewEditVisible = false;

  @Output() isNewEditVisibleChange = new EventEmitter<boolean>();

  @Input() isEdit = false;

  validateForm: FormRecord<FormControl<string>>;

  listOfControl: Array<{ id: number; controlInstance: string }> = [];

  constructor(
    private ref: ChangeDetectorRef,
    private methodologyService: MethodologyService,
    private fb: NonNullableFormBuilder,
    private openaiService: OpenaiService
  ) {
    this.validateForm = this.fb.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.addRubric();
  }

  onGenerateRubricsClick() {
    const description = this.validateForm.value['description'];
    const request1: GetAnswerRequest = {
      messageList: [
        {
          role: 'user',
          content: `Output the rubrics of the following sales methodology: \n\n ${description}`,
        },
      ],
      config: {
        openaiModel: OpenaiModel.gpt4,
        functions: [
          {
            name: 'output_rubrics_for_methodology',
            description: 'Output the rubrics of the sales methodology',
            parameters: {
              type: 'object',
              properties: {
                rubrics: {
                  type: 'array',
                  items: {
                    type: 'string',
                  },
                },
              },
            },
          },
        ],
      },
    };

    this.openaiService
      .getAnswer$(request1)
      .pipe(take(1))
      .subscribe((result) => {
        console.log(result);

        const firstValue = this.validateForm.value[this.listOfControl[0].controlInstance];
        if (!firstValue) {
          this.removeRubric(this.listOfControl[0]);
        }

        result.rubrics.forEach((c: string) => {
          this.addRubric(c);
          console.log('addRubric');
        });

        this.listOfControl = [...this.listOfControl];

        this.ref.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const chng1 = changes['isEdit'];
    if (chng1?.previousValue !== chng1?.currentValue) {
      if (chng1?.currentValue === false) {
        this.validateForm = this.fb.group({
          title: ['', [Validators.required]],
          description: ['', [Validators.required]],
        });
        const needToRemove = [...this.listOfControl];

        needToRemove?.forEach((c) => {
          this.removeRubric(c);
        });

        this.addRubric();
      }
    }

    for (const propName in changes) {
      if (propName === 'selectedItem') {
        const chng = changes[propName];
        if (chng.previousValue !== chng.currentValue) {
          const selectedItem = chng.currentValue;

          this.validateForm.patchValue({
            title: selectedItem.title ?? '',
            description: selectedItem.description ?? '',
          });

          // Remove all fields
          const needToRemove = [...this.listOfControl];

          needToRemove?.forEach((c) => {
            this.removeRubric(c);
          });

          if (selectedItem?.subData?.rubrics?.length > 0) {
            selectedItem?.subData?.rubrics?.forEach((c: string) => {
              this.addRubric(c);
            });
          }

          this.listOfControl = [...this.listOfControl];
        }
      }
    }
  }

  addField(e?: MouseEvent): void {
    e?.preventDefault();

    this.addRubric();
  }

  private addRubric(value?: string) {
    const id = this.listOfControl.length > 0 ? this.listOfControl[this.listOfControl.length - 1].id + 1 : 0;

    const control = {
      id,
      controlInstance: `rubric${id}`,
    };
    const index = this.listOfControl.push(control);

    this.validateForm.addControl(
      this.listOfControl[index - 1].controlInstance,
      this.fb.control(value ?? '', Validators.required)
    );
  }

  removeField(i: { id: number; controlInstance: string }, e: MouseEvent): void {
    e.preventDefault();
    if (this.listOfControl.length > 1) {
      this.removeRubric(i);
    }
  }

  private removeRubric(i: { id: number; controlInstance: string }) {
    const index = this.listOfControl.indexOf(i);
    this.listOfControl.splice(index, 1);
    this.validateForm.removeControl(i.controlInstance);
  }

  onGenerateDescriptionClick() {
    const title = this.validateForm.value['title'];

    if (title?.trim()) {
      const request1: GetAnswerRequest = {
        messageList: [
          {
            role: 'user',
            content: `Output the stages of the sales methodology of "${title}". Also provide 5 example questions.`,
          },
        ],
        config: {
          openaiModel: OpenaiModel.gpt4,
        },
      };

      this.openaiService
        .getAnswer$(request1)
        .pipe(take(1))
        .subscribe((result) => {
          this.validateForm.patchValue({
            description: result,
          });

          const request2: GetAnswerRequest = {
            messageList: [
              {
                role: 'user',
                content: `Output the rubrics of the following sales methodology: \n\n ${result}`,
              },
            ],
            config: {
              openaiModel: OpenaiModel.gpt4,
              functions: [
                {
                  name: 'output_rubrics_for_methodology',
                  description: 'Output the rubrics of the sales methodology',
                  parameters: {
                    type: 'object',
                    properties: {
                      rubrics: {
                        type: 'array',
                        items: {
                          type: 'string',
                        },
                      },
                    },
                  },
                },
              ],
            },
          };

          this.openaiService
            .getAnswer$(request2)
            .pipe(take(1))
            .subscribe((result) => {
              const firstValue = this.validateForm.value[this.listOfControl[0].controlInstance];
              if (!firstValue) {
                this.removeRubric(this.listOfControl[0]);
              }

              result.rubrics.forEach((c: string) => {
                this.addRubric(c);
              });

              this.listOfControl = [...this.listOfControl];

              this.ref.detectChanges();
            });
        });
    }
  }

  submitForm() {
    if (this.validateForm.valid) {
      this.isNewEditVisible = false;
      this.isNewEditVisibleChange.emit(false);

      const title = this.validateForm.value['title'];
      const description = this.validateForm.value['description'];
      const rubrics: string[] = [];

      Object.values(this.validateForm.value).forEach((value, index) => {
        if (index > 1) {
          if (value) {
            rubrics.push(value);
          }
        }
      });

      const subData = {
        rubrics: rubrics,
      };

      if (this.isEdit) {
        this.selectedItem!.title = title;
        this.selectedItem!.description = description;

        this.selectedItem!.subData = subData;

        this.methodologyService.updateMethodology$(this.selectedItem!).pipe(take(1)).subscribe();
      } else {
        this.methodologyService
          .addMethodology$(title ?? '', description ?? '', subData)
          .pipe(take(1))
          .subscribe();
      }
    } else {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  onIsNewEditVisibleChange(event: boolean) {
    this.isNewEditVisibleChange.emit(event);
  }

  onCancelClick() {
    this.isNewEditVisible = false;

    this.isNewEditVisibleChange.emit(false);
  }
}
